import React, { Fragment, useCallback, useState, useEffect } from 'react'
import { Grid, Typography } from '@mui/material'
import { store } from 'reduxStore'
import { AccountFormField } from 'components/Common/AccountFormLayout'
import TextField from 'components/Common/TextField'
import { Formik, Field, Form } from 'formik'
import * as yup from 'yup'
import { passwordHelperText } from 'constants/index'
import FormikTextField from 'components/Common/FormikTextField'
import Button from 'components/Common/Button'
import ZendeskLink from 'components/Common/ZendeskLink'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import api from 'api'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import routes from 'routes'
import { Dialog, DialogActions, DialogContent, Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { PRIMARY_BUTTON, DISABLED_BUTTON } from 'themes/theme'

export const passwordFieldSchema = yup
  .string()
  .min(8, 'Password must be at least 8 characters')
  .required('Password is required')
  .matches(/[a-z]+/, 'Password requires lowercase letters')
  .matches(/[A-Z]+/, 'Password requires uppercase letters')
  .matches(/[0-9]+/, 'Password requires numbers')
  .matches(/[!@#$%^&*)(+=.<>{}/[\\\]:;'"|~`_-]+/, 'Password requires special characters')

export const passwordConfirmFieldSchema = yup
  .string()
  .required('Password confirm is required')
  .test('passwords-match', 'Passwords must match', (value, context) => {
    return context.parent.newPassword === value
  })

export const oldPasswordFieldSchema = yup
  .string()
  .required('Old Password is a required field')
  .min(
    8,
    'password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special',
  )
  .matches(/[a-z]+/, 'Password requires lowercase letters')
  .matches(/[A-Z]+/, 'Password requires uppercase letters')
  .matches(/[0-9]+/, 'Password requires numbers')
  .matches(/[!@#$%^&*)(+=.<>{}/[\\\]:;'"|~`_-]+/, 'Password requires special characters')

const schema = yup.object({
  oldPassword: oldPasswordFieldSchema,
  newPassword: passwordFieldSchema,
  newPasswordConfirm: passwordConfirmFieldSchema,
})

const sx = {
  gridItem: {
    padding: '0 30px',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '1px',
      height: '100%',
      backgroundColor: 'rgb(217,220,220)',
    },
  },
  validationBox: {
    color: 'var(--primary)',
    fontFamily: 'poppins',
  },
  validation: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '22px',
  },
  validationContent: {
    fontSize: '12px',
    fontWeight: 'lighter',
    display: 'flex',
  },
}

const PasswordText = styled(Box)(() => ({
  fontWeight: 'lighter',
  fontFamily: 'poppins',
  fontSize: '12px',
}))

const initialValues = {
  oldPassword: '',
  newPassword: '',
  newPasswordConfirm: '',
}

const Security = props => {
  const user = store.getState().userReducer.user
  const { accountType } = user
  const [dialogOpen, setDialogOpen] = useState(false)
  const dispatch = useDispatch()
  const [countDownLogout, setCountDownLogout] = useState(5)
  const navigate = useNavigate()

  const handleClose = useCallback(() => {
    setDialogOpen(false)
    dispatch({ type: 'userSignOut' })
    api.signOutOfAllDevices().then(() => {
      navigate(routes.auth.signOut)
    })
  }, [dispatch, navigate])

  const handleOk = useCallback(() => {
    setDialogOpen(false)
    api.signOutOfAllDevices().then(() => {
      navigate(routes.auth.signOut)
    })
  }, [navigate])

  const onFormSubmit = useCallback(async (values, formikHelpers) => {
    formikHelpers.setSubmitting(true)

    try {
      await api.changePassword(values.oldPassword, values.newPassword)
      setDialogOpen(true)
    } catch (error) {
      switch (error.name) {
        case 'NotAuthorizedException':
          formikHelpers.setErrors({ oldPassword: 'Old password isn’t correct, please try again' })
          break
        default:
          formikHelpers.setErrors({ newPasswordConfirm: error.message })
      }
    } finally {
      formikHelpers.setSubmitting(false)
    }
  }, [])
  useEffect(() => {
    if (dialogOpen) {
      let myInterval = setInterval(() => {
        if (countDownLogout > 0) {
          setCountDownLogout(prevState => prevState - 1)
        } else {
          handleClose()
          clearInterval(myInterval)
        }
      }, 1000)
      return () => {
        clearInterval(myInterval)
      }
    }
  })
  if (!user) return null

  return (
    <Fragment>
      <Dialog
        BackdropProps={{ style: { backgroundColor: 'inherit' } }}
        PaperProps={{ style: {} }}
        open={dialogOpen}
        keepMounted
        onClose={handleOk}
      >
        <DialogContent>
          <Typography variant="body2" style={{ color: '#25245F' }}>
            Password has been successfully changed. You will be automatically logged out in {countDownLogout} seconds.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button onClick={handleOk} style={{ marginRight: '20px' }}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Formik validationSchema={schema} onSubmit={onFormSubmit} initialValues={initialValues} validateOnBlur={true}>
        {({ isSubmitting, isValid, dirty }) => (
          <Form autoComplete="off">
            <Grid container className="customAuthInput">
              <Grid item xs={12} md={5}>
                <Grid margin="10%" paddingLeft="12%">
                  <AccountFormField>
                    <TextField label="Email" disabled fullWidth borderLess value={user.email} />
                  </AccountFormField>
                  <AccountFormField style={{ marginBottom: '10px' }}>
                    <Field name="oldPassword">
                      {({ field, meta }) => (
                        <FormikTextField
                          {...field}
                          type="password"
                          label="Old Password"
                          required
                          fullWidth
                          borderLess
                          autoComplete="new-password"
                          value={user.oldPassword}
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          disabled={isSubmitting}
                        ></FormikTextField>
                      )}
                    </Field>
                  </AccountFormField>
                  <AccountFormField style={{ marginBottom: '30px' }}>
                    <Field name="newPassword">
                      {({ field, meta }) => (
                        <FormikTextField
                          {...field}
                          type="password"
                          label="New Password"
                          required
                          fullWidth
                          borderLess
                          autoComplete="off"
                          error={Boolean(meta.touched && meta.error)}
                          helperText={(meta.touched && meta.error) || <PasswordText>{passwordHelperText}</PasswordText>}
                          disabled={isSubmitting}
                        />
                      )}
                    </Field>
                  </AccountFormField>
                  <AccountFormField>
                    <Field name="newPasswordConfirm">
                      {({ field, meta }) => (
                        <FormikTextField
                          {...field}
                          type="password"
                          label="New Password Confirm"
                          required
                          fullWidth
                          borderLess
                          autoComplete="off"
                          error={Boolean(meta.touched && meta.error)}
                          disabled={isSubmitting}
                        />
                      )}
                    </Field>
                  </AccountFormField>
                  <Button
                    submit
                    loading={isSubmitting}
                    type={!isValid || !dirty ? DISABLED_BUTTON : PRIMARY_BUTTON}
                    sx={{ width: { xs: '50%', sm: '50%', md: '40%', lg: '40%', xl: '40%' }, marginTop: '20px' }}
                  >
                    Update
                  </Button>
                </Grid>
              </Grid>
              <Grid item xs={1} sx={sx.gridItem}></Grid>
              <Grid item xs={12} md={5}>
                <Grid margin="10%" paddingLeft="12%">
                  {accountType !== 'free' && (
                    <Box sx={sx.validationBox}>
                      {user?.kycValidated && (
                        <Fragment>
                          <Box sx={sx.validation}>Validation</Box>
                          <Box sx={sx.validationContent}>
                            <FontAwesomeIcon size="xl" icon={faUser} />
                            <Box marginLeft="5%">{`Identity Verified on ${moment(user?.kycValidated).format(
                              'MM/DD/YYYY',
                            )}`}</Box>
                          </Box>
                        </Fragment>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <ZendeskLink />
          </Form>
        )}
      </Formik>
    </Fragment>
  )
}

export default Security
